import {
  AccessorialCode,
  Optional,
  ZonedDateTimeRange,
} from '@rygen/rygen-ui/model';
import { distinctBy } from '@rygen/rygen-ui/util';

import i18n from '@/i18n';

import { EmbeddableLocation } from '../EmbeddableLocation';
import {
  SHIPMENT_STOP_DROP_OFF_TYPES,
  SHIPMENT_STOP_PICKUP_TYPES,
  ShipmentStopType,
} from '../shipment/ShipmentStopType';
import { ShipOrderShippingPoint } from '../ShipOrderShippingPoint';
import { ShippingPoint } from '../ShippingPoint';

import { ValidShipOrderShippingPoint } from './ValidShipOrderShippingPoint';

const TYPE = 'ProposedShipment';

type ConstructableProposedShipmentStop = Partial<ProposedShipmentStop> &
  Required<Pick<ProposedShipmentStop, 'shipmentIdentifier' | 'sequenceNumber'>>;

export class ProposedShipmentStop implements ShippingPoint {
  readonly shipOrderShippingPoints: ValidShipOrderShippingPoint[] = [];

  shipmentIdentifier: number;
  sequenceNumber: number;

  _stopType?: ShipmentStopType;
  _location?: EmbeddableLocation;
  _requestedAppointmentRange?: ZonedDateTimeRange;

  constructor(opts: ConstructableProposedShipmentStop) {
    this.shipmentIdentifier = opts.shipmentIdentifier;
    this.sequenceNumber = opts.sequenceNumber;

    if (opts._stopType ?? opts.stopType) {
      this._stopType = opts._stopType ?? opts.stopType;
    }

    if (opts._location ?? opts.location) {
      this._location = new EmbeddableLocation(opts._location ?? opts.location);
    }

    if (opts._requestedAppointmentRange ?? opts.requestedAppointmentRange) {
      this.requestedAppointmentRange = new ZonedDateTimeRange(
        opts._requestedAppointmentRange ?? opts.requestedAppointmentRange,
      );
    }

    opts.shipOrderShippingPoints?.forEach((x) =>
      this.addShipOrderShippingPoint(x),
    );
  }

  static isProposedShipmentStop(
    location: ShippingPoint,
  ): location is ProposedShipmentStop {
    return location.type === TYPE;
  }

  get type() {
    return TYPE;
  }

  get stopType(): ShipmentStopType {
    if (this._stopType) {
      return this._stopType;
    }

    const stopType = this.shipOrderShippingPoints
      .map((x) => x.shippingPointType)
      .reduce((agg, curr, currIndex) => {
        const currShipmentStopType = ShipmentStopType[curr];
        if (currIndex === 0) {
          return currShipmentStopType;
        } else if (agg !== currShipmentStopType) {
          return ShipmentStopType.DROP_OFF_PICKUP;
        } else {
          return agg;
        }
      }, ShipmentStopType.UNKNOWN);
    return stopType;
  }

  set stopType(stopType: ShipmentStopType) {
    if (
      stopType !== ShipmentStopType.PICKUP &&
      stopType !== ShipmentStopType.DROP_OFF
    ) {
      console.warn(
        `ProposedShipmentStop does not support stop type ${stopType}`,
      );
      return;
    }

    this._stopType = stopType;
  }

  get location(): EmbeddableLocation {
    return (
      this._location ??
      this.shipOrderShippingPoints.find((x) => !!x)?.location ??
      new EmbeddableLocation()
    );
  }

  set location(location: Optional<EmbeddableLocation>) {
    this._location = location;
  }

  get requestedAppointmentRange(): ZonedDateTimeRange {
    return (
      this._requestedAppointmentRange ??
      this.shipOrderShippingPoints.find((x) => !!x)?.requestedAppointment ??
      new ZonedDateTimeRange()
    );
  }

  set requestedAppointmentRange(
    requestedAppointmentRange: Optional<ZonedDateTimeRange>,
  ) {
    this._requestedAppointmentRange = requestedAppointmentRange;
  }

  get requestedAppointmentEarliest() {
    return this.requestedAppointmentRange.start;
  }

  get requestedAppointmentLatest() {
    return this.requestedAppointmentRange.end;
  }

  get requestedAccessorials(): AccessorialCode[] {
    return this.shipOrderShippingPoints
      .filter((x) => !!x)
      .flatMap((s) => s.requestedAccessorials)
      .filter(distinctBy('naturalId'));
  }

  get shipOrderShippingPointIds(): number[] {
    return this.shipOrderShippingPoints.map((x) => x.id);
  }

  /**
   * Useful when vue needs a :key for a list, but id or sequence number or the
   * index isn't appropriate
   * (e.g. in a sortablejs list)
   */
  get key(): string {
    return JSON.stringify(this);
  }

  get isInProgress() {
    return false;
  }

  get isPickup() {
    return (
      !!this.stopType && SHIPMENT_STOP_PICKUP_TYPES.includes(this.stopType)
    );
  }

  get isDropOff() {
    return (
      !!this.stopType && SHIPMENT_STOP_DROP_OFF_TYPES.includes(this.stopType)
    );
  }

  get stopLabel(): string {
    const stopName = [
      this.location.locationName,
      this.location.city,
      this.location.state,
    ]
      .filter((x) => !!x?.trim())
      .join(', ');

    const stopNumber = i18n.global.t('common.paramNum', {
      param: i18n.global.t('shipments.stops'),
      num: this.sequenceNumber,
    });

    return [stopNumber, stopName].join(' - ');
  }

  get isPlaceholderStop(): boolean {
    return !this._location && !this.shipOrderShippingPoints.length;
  }

  hasShipOrderShippingPoint(
    shipOrderShippingPoint: ShipOrderShippingPoint,
  ): boolean {
    const shipOrderShippingPointId = shipOrderShippingPoint.id;

    return (
      !!shipOrderShippingPointId &&
      this.shipOrderShippingPointIds.includes(shipOrderShippingPointId)
    );
  }

  isResolved(_requireBothDates: boolean = false) {
    return false;
  }

  addShipOrderShippingPoint(
    shipOrderShippingPoint: ShipOrderShippingPoint,
  ): void {
    const validShipOrderShippingPoint = new ValidShipOrderShippingPoint(
      shipOrderShippingPoint,
    );
    this.shipOrderShippingPoints.push(validShipOrderShippingPoint);
  }

  toJSON() {
    // `JSON.stringify` ignores getters so manually add them here if needed
    return {
      sequenceNumber: this.sequenceNumber,
      stopType: this.stopType,
      location: this.location,
      shipOrderShippingPointIds: this.shipOrderShippingPointIds,
      requestedAppointmentRange: this.requestedAppointmentRange,
    };
  }
}
